'use client';

import PageContainer from '@/layout/page-container';
import Section from '@/layout/section';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/modules/marketing/components/page-shared';
import { Animated, StaggerContainer } from '@/components/ui/animated';

export default function PricingPage() {
  return (
    <PageContainer>
      <Animated animation="fade-up" duration={800}>
        <PageHero
          kicker="Investment"
          title="Pricing"
          subtitle="Transparent pricing for quality clinical rotations. We work with you to ensure successful placement."
        />
      </Animated>
      <Section>
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Pricing Information */}
          <Animated animation="fade-up" delay={100}>
            <div className="bg-white border border-[var(--brand-border)] rounded-xl p-8 space-y-6 hover:shadow-lg transition-shadow duration-300">
              <div className="space-y-4 text-base leading-relaxed text-gray-700">
                <p>
                  Our normal rate for rotations is <strong>$15/hr</strong>. The minimum price for rotations is <strong>$1200 (80hrs)</strong>. The rate may vary depending on the specific preceptor/site available.
                </p>
                <p>
                  We charge a refundable deposit to start working on your application and we assure you will be approved or we will refund the whole amount you paid.
                </p>
                <p>
                  We offer discounts for students that complete multiple rotations with us. We also offer referral discounts for successful rotations completed by referred students.
                </p>
                <p>
                  If you have any questions, please don't hesitate to <Link href="/contact" className="text-blue-600 hover:underline font-semibold">contact us</Link>!
                </p>
              </div>
            </div>
          </Animated>

          {/* Process Section */}
          <Animated animation="fade-up" delay={200}>
            <div className="bg-white border border-[var(--brand-border)] rounded-xl p-8 space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">How does the placement process work?</h2>
              
              <StaggerContainer staggerDelay={100} animation="fade-left" className="space-y-6">
                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold group-hover:scale-110 transition-transform">1</div>
                  <div>
                    <p className="text-gray-700">
                      You select a preceptor from the system, check the rotation details and authorize the deposit payment.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold group-hover:scale-110 transition-transform">2</div>
                  <div>
                    <p className="text-gray-700">
                      The team reviews the application and confirms the details. Once it's confirmed, we charge the <strong>$200 deposit</strong>. This deposit is refundable in many cases, as long as the preceptor has not been approved. If there is any problem with the application, we will get in contact with you to find another suitable option.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold group-hover:scale-110 transition-transform">3</div>
                  <div>
                    <p className="text-gray-700">
                      After payment, we share the provider name/site details, and confirm availability with the preceptor.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold group-hover:scale-110 transition-transform">4</div>
                  <div>
                    <p className="text-gray-700">
                      Once confirmed, we send everything needed to submit your application (CV, license, email, phone number, etc). To access the signed paperwork, you will have to pay <strong>15% of the remaining amount</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold group-hover:scale-110 transition-transform">5</div>
                  <div>
                    <p className="text-gray-700">
                      You submit your application and keep us posted on your approval.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold group-hover:scale-110 transition-transform">6</div>
                  <div>
                    <p className="text-gray-700">
                      After approval, we help you get in contact with your preceptor and you pay the remaining balance, due before your clinical start date.
                    </p>
                  </div>
                </div>
              </StaggerContainer>
            </div>
          </Animated>

          {/* CTA */}
          <Animated animation="fade-up" delay={300}>
            <div className="text-center">
              <Link href="/contact">
                <Button size="lg" className="px-8 hover:scale-105 transition-transform">
                  Have Questions? Contact Us
                </Button>
              </Link>
            </div>
          </Animated>
        </div>
      </Section>
    </PageContainer>
  );
}
